import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg, errRef } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/app/Bits";
import { FactorForm } from "@/components/security/Mfa";
import { StateScreen, StateButton } from "@/components/app/AppStates";
import { MailLink } from "@/lib/contacts";
import { AppleSignInButton } from "@/components/security/AppleSignIn";

const inp = "w-full h-12 rounded-lg border border-input bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-stone-900/20";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBF9F5]">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1641536618422-2cf0bbd10014?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" alt="Open restaurant kitchen" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-stone-950/55" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-display text-5xl">86'd</div>
          <p className="mt-3 text-stone-200 max-w-md">Built by a working restaurant manager. Less administration, more leadership.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="font-display text-3xl lg:hidden">86'd</Link>
          <h1 className="font-display text-3xl md:text-4xl mt-6 lg:mt-0" data-testid="auth-title">{title}</h1>
          {subtitle && <p className="text-stone-600 mt-2 text-sm">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

const G_KEY = "86d_google_attempt";
export const clearGoogleAttempt = () => sessionStorage.removeItem(G_KEY);

// Shown only when a Google attempt started in this tab and the user came back without a session.
function GoogleRecovery() {
  const [show] = useState(() => { const t = Number(sessionStorage.getItem(G_KEY)); sessionStorage.removeItem(G_KEY); return t && Date.now() - t < 15 * 60 * 1000; });
  if (!show) return null;
  return (
    <div role="alert" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="google-recovery">
      Google sign-in didn't finish. If Google showed an error (for example “Invalid state parameter”), start again from this page in the same tab and don't use the back button. On iPhone Safari, turning off “Prevent Cross-Site Tracking” for this sign-in or using email and password also works.
    </div>
  );
}

function GoogleBtn() {
  const [busy, setBusy] = useState(false);
  return (<>
  <GoogleRecovery />
  <button type="button" data-testid="google-login-btn" disabled={busy} aria-busy={busy} onClick={() => {
    if (busy) return;
    setBusy(true);
    sessionStorage.setItem(G_KEY, String(Date.now()));
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }} className="w-full h-12 rounded-lg border border-input bg-white font-medium flex items-center justify-center gap-3 hover:bg-stone-50 disabled:opacity-70">
    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>}
    {busy ? "Opening Google…" : "Continue with Google"}
  </button>
  </>);
}

const Divider = () => <div className="flex items-center gap-3 my-5 text-xs text-stone-400"><div className="h-px flex-1 bg-stone-200" />or<div className="h-px flex-1 bg-stone-200" /></div>;

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return [busy, run];
}

const Submit = ({ busy, children, testid }) => (
  <button disabled={busy} data-testid={testid} className="w-full h-12 rounded-lg bg-stone-900 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}
  </button>
);

const lbl = "block text-sm font-medium text-stone-700 mb-1";
const Field = ({ id, label, hint, error, ...p }) => (
  <div>
    <label htmlFor={id} className={lbl}>{label}</label>
    <input id={id} className={`${inp} ${error ? "border-rose-500" : ""}`} aria-invalid={!!error} aria-describedby={hint || error ? `${id}-msg` : undefined} {...p} />
    {(hint || error) && <div id={`${id}-msg`} className={`text-xs mt-1 ${error ? "text-rose-700 font-medium" : "text-stone-500"}`} data-testid={`${id}-msg`}>{error || hint}</div>}
  </div>
);

export function Login() {
  const { apply, expired } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [mfa, setMfa] = useState(null);
  const done = (data) => { if (data.user?.role === "guest") { window.location.assign("/discover/account"); return; } apply(data); nav(data.user.role === "superadmin" ? "/admin" : loc.state?.from || "/app/dashboard"); };
  const [busy, run] = useSubmit(async () => {
    const { data } = await api.post("/auth/login", f);
    if (data.mfa_required) return setMfa(data.methods);
    done(data);
  });
  if (mfa) return (
    <Shell title="Two-step verification" subtitle="Confirm it's you to finish signing in.">
      <FactorForm methods={mfa} passkeyOptionsPath="/auth/mfa/passkey/options" label="Sign in" submit={async (p) => done((await api.post("/auth/mfa/verify", p)).data)} />
      <button onClick={() => setMfa(null)} className="mt-6 text-sm underline" data-testid="mfa-back">Back</button>
    </Shell>
  );
  return (
    <Shell title="Welcome back" subtitle="Log in to your restaurant.">
      {(expired || loc.state?.expired) && <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="status" data-testid="session-expired-notice">Your session ended. Sign in again to continue.</div>}
      <GoogleBtn /><AppleSignInButton onResult={(data) => (data.mfa_required ? setMfa(data.methods) : done(data))} /><Divider />
      <form onSubmit={run} className="space-y-3" data-testid="login-form">
        <Field id="login-email" label="Email" type="email" required autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email" />
        <Field id="login-password" label="Password" type="password" required autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password" />
        <Submit busy={busy} testid="login-submit">Log in</Submit>
      </form>
      <div className="flex justify-between text-sm mt-5">
        <Link to="/forgot-password" className="underline" data-testid="forgot-link">Forgot password?</Link>
        <Link to="/signup" className="font-semibold" data-testid="signup-link">Start free trial</Link>
      </div>
      <div className="mt-8 rounded-lg bg-[#F3EFE6] p-4 text-xs text-stone-600" data-testid="demo-hint">
        <b>Explore the demo:</b> owner@juniperandoak.com · Demo86d! <span className="block mt-1 text-stone-500">Shared sample restaurant with fictional data — security settings are locked and two-step verification is not enforced on it.</span>
      </div>
    </Shell>
  );
}

export function Signup() {
  const { apply } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const interest = sp.get("plan") || "";
  const [f, setF] = useState({ name: "", restaurant: "", email: "", password: "", accept_pilot: false });
  const [pwErr, setPwErr] = useState("");
  const [legalErr, setLegalErr] = useState("");
  const [busy, run] = useSubmit(async () => {
    if (f.password.length < 8) { setPwErr("Use at least 8 characters"); return; }
    setPwErr("");
    if (!f.accept_pilot) { setLegalErr("Tick the box to confirm you're joining the pilot."); document.getElementById("signup-accept-pilot")?.focus(); return; }
    setLegalErr("");
    const { data } = await api.post("/auth/register", { ...f, plan_interest: interest, interval_interest: sp.get("interval") || "" });
    if (sp.get("services")) localStorage.setItem("86d_services_interest", sp.get("services"));
    apply(data);
    nav("/onboarding");
  });
  const names = { essential: "Essential", ops_pro: "Operations Pro", group: "Group", founding: "Founding Restaurant" };
  return (
    <Shell title="Start your 14-day free trial" subtitle="No credit card required. Start with guided setup and configure your restaurant at your own pace.">
      {names[interest] && <div className="mb-5 rounded-lg bg-[#F3EFE6] p-3 text-sm" data-testid="signup-plan-interest">You picked <b>{names[interest]}</b>. Start with a free trial of every feature, then check out whenever you're ready. You don't have to decide today.</div>}
      <GoogleBtn /><p className="text-[11px] text-stone-500 mt-2" data-testid="signup-google-pilot-note">Continuing with Google creates a pilot account and acknowledges the same draft pilot Terms and Privacy Notice from 86 Operations Restaurant Management Software LLC, which are awaiting legal review and publication.</p><Divider />
      <form onSubmit={run} className="space-y-3" data-testid="signup-form">
        <Field id="signup-name" label="Your name" required autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="signup-name" />
        <Field id="signup-restaurant" label="Restaurant name" required autoComplete="organization" value={f.restaurant} onChange={(e) => setF({ ...f, restaurant: e.target.value })} data-testid="signup-restaurant" />
        <Field id="signup-email" label="Work email" type="email" required autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="signup-email" />
        <Field id="signup-password" label="Password" type="password" required minLength={8} autoComplete="new-password" hint="At least 8 characters" error={pwErr} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="signup-password" />
        <div className="rounded-lg bg-[#F3EFE6] p-3 space-y-2" data-testid="signup-legal">
          <p className="text-xs text-stone-700"><b>Pilot launch.</b> 86'd is in a pilot that is still in active development. The <Link to="/legal/terms" className="underline">Terms of Service</Link> and <Link to="/privacy" className="underline">Privacy Notice</Link> are pilot versions that are subject to legal revision. We'll ask the account owner to accept the final versions when they're published.</p>
          <label className="flex items-start gap-2 text-xs text-stone-800"><input id="signup-accept-pilot" type="checkbox" className="mt-0.5 h-4 w-4" checked={f.accept_pilot} onChange={(e) => setF({ ...f, accept_pilot: e.target.checked })} aria-invalid={!!legalErr} aria-describedby={legalErr ? "signup-legal-error" : undefined} data-testid="signup-accept-pilot" />
            <span>I'm joining the 86'd pilot and acknowledge the draft pilot Terms of Service and Privacy Notice from 86 Operations Restaurant Management Software LLC. They are drafts awaiting legal review and publication and are not yet final; I'll be asked to accept the final versions when they're published.</span></label>
          {legalErr && <p id="signup-legal-error" role="alert" className="text-xs text-rose-700" data-testid="signup-legal-error">{legalErr}</p>}
        </div>
        <Submit busy={busy} testid="signup-submit">Create my account</Submit>
      </form>
      <p className="text-sm mt-5">Already have an account? <Link to="/login" className="font-semibold underline">Log in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, run] = useSubmit(async () => { await api.post("/auth/forgot-password", { email }); setSent(true); });
  return (
    <Shell title="Reset your password" subtitle="We'll send a reset link to your email.">
      {sent ? <div className="card-surface p-5 text-sm space-y-2" data-testid="forgot-sent"><p>If an account exists for that email, we'll send instructions within a few minutes. Reset links expire in 1 hour and work once.</p><p className="text-stone-600">Not there? Check spam, or request a new link. If you usually sign in with Google, use “Continue with Google” on the sign-in page — there's no 86'd password to reset.</p><Link to="/login" className="underline" data-testid="forgot-back-login">Back to sign in</Link><p className="text-stone-500">Still stuck? Email <MailLink k="support" />.</p></div> : (
        <form onSubmit={run} className="space-y-3">
          <input className={inp} type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          <Submit busy={busy} testid="forgot-submit">Send reset link</Submit>
        </form>
      )}
      <Link to="/login" className="text-sm underline mt-5 inline-block">Back to log in</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, run] = useSubmit(async () => {
    await api.post("/auth/reset-password", { token: sp.get("token"), password: pw });
    toast.success("Password updated — please log in");
    nav("/login");
  });
  return (
    <Shell title="Choose a new password">
      <form onSubmit={run} className="space-y-3">
        <input className={inp} type="password" required minLength={8} placeholder="New password (8+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" />
        <Submit busy={busy} testid="reset-submit">Update password</Submit>
      </form>
    </Shell>
  );
}

export function InviteAccept() {
  const { token } = useParams();
  const { apply } = useAuth();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  useEffect(() => {
    api.get(`/invites/${token}`).then(({ data }) => { setInv(data); setF((s) => ({ ...s, name: data.name || "", email: data.email || "" })); }).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const [busy, run] = useSubmit(async () => {
    const { data } = await api.post(`/invites/${token}/accept`, f);
    apply(data);
    nav("/app/today");
  });
  if (err) return <Shell title="Invitation unavailable" subtitle={err}><Link to="/login" className="underline">Go to log in</Link></Shell>;
  if (!inv) return <Loading />;
  return (
    <Shell title={`Join ${inv.restaurant}`} subtitle={`You've been invited as ${{ gm: "General Manager", agm: "Assistant Manager", dept: "Department Manager" }[inv.role]}.`}>
      <form onSubmit={run} className="space-y-3" data-testid="invite-form">
        <input className={inp} required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" />
        <input className={inp} type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email" />
        <input className={inp} type="password" required minLength={8} placeholder="Create a password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="invite-password" />
        <Submit busy={busy} testid="invite-submit">Join and open 86'd</Submit>
      </form>
    </Shell>
  );
}

function landingFor(data) {
  if (data.user.role === "superadmin") return "/admin";
  if (data.org && !data.org.onboarding_complete && data.user.role === "owner") return "/onboarding";
  return "/app/dashboard";
}

export function AuthCallback() {
  const { apply } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  const [mfa, setMfa] = useState(null);
  const [fail, setFail] = useState(null);
  const finish = useCallback((data) => {
    if (!data?.user) throw new Error("Sign-in didn't return an account");
    apply(data);
    nav(landingFor(data), { replace: true });
  }, [apply, nav]);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) { setFail({ text: "The Google sign-in link was incomplete." }); return; }
    api.post("/auth/google", { session_id: sessionId, ...(sessionStorage.getItem("86d_guest_google") ? { account_type: "guest" } : {}) }, { timeout: 25000 }).then(({ data }) => {
      clearGoogleAttempt();
      if (sessionStorage.getItem("86d_guest_google")) { sessionStorage.removeItem("86d_guest_google"); if (data.user?.role === "guest") { window.location.replace("/discover/account"); return; } }
      if (data.mfa_required) return setMfa(data.methods || ["recovery"]);
      finish(data);
    }).catch((e) => setFail({ text: e.response ? errMsg(e) : "We couldn't reach 86'd. Check your connection and try again.", ref: errRef(e) }));
  }, [finish]);
  if (fail) return (
    <StateScreen kind="expired" testid="google-callback-error" title="Google sign-in didn't finish" text={fail.text} refId={fail.ref}
      actions={<><StateButton primary onClick={() => nav("/login", { replace: true })} data-testid="google-callback-retry">Back to sign in</StateButton></>} />
  );
  if (mfa) return (
    <Shell title="Two-step verification" subtitle="Google confirmed your email. Now confirm it's you to finish signing in.">
      <FactorForm methods={mfa} passkeyOptionsPath="/auth/mfa/passkey/options" label="Sign in" submit={async (p) => finish((await api.post("/auth/mfa/verify", p)).data)} />
      <button onClick={() => nav("/login", { replace: true })} className="mt-6 text-sm underline" data-testid="mfa-back">Cancel and go back</button>
    </Shell>
  );
  return <div className="min-h-screen flex items-center justify-center" data-testid="google-callback-loading"><Loading label="Signing you in…" /></div>;
}
